import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';

import { ativarSecretario, desativarSecretario, type Configuracao } from '@/api/secretario';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

import { ProntidaoChecklist } from './ProntidaoChecklist';
import { WizardActions } from './WizardActions';

interface StepProps {
  config: Configuracao | null;
  onNext: () => void;
  onBack: () => void;
  isFirst: boolean;
}

/** Passo 5 — revisão final + ativar/desativar. O botão só liga com a prontidão verde
 * (número, OAB, certificado e uma área); o resto é a personalização, opcional, na aba Áreas. */
export function AtivacaoStep({ config, onBack, isFirst }: StepProps) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();

  const mutation = useMutation({
    mutationFn: (proximo: boolean) => (proximo ? ativarSecretario() : desativarSecretario()),
    onSuccess: (_data, proximo) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.secretario.config });
      showSuccess(proximo ? 'Secretário ativado.' : 'Secretário desativado.');
    },
    onError: (error) => notifyMutationError(error, showError),
  });

  if (!config) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size={32} label="Carregando" />
      </div>
    );
  }

  const { ativo, prontidao } = config;
  const preAtendimento = prontidao.por_area.some((a) => !a.pronta);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ativação</CardTitle>
        <CardDescription>
          {ativo
            ? 'Seu Secretário está ativo e atendendo pelo WhatsApp.'
            : 'Revise o checklist. Quando tudo estiver pronto, ative o Secretário.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {ativo && (
          <Alert className="border-emerald-500/30 bg-emerald-500/5">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <AlertDescription className="text-emerald-700">Secretário ativo.</AlertDescription>
          </Alert>
        )}

        <ProntidaoChecklist prontidao={prontidao} ocultarConfirmacao={ativo} />

        {preAtendimento && (
          <p className="text-sm text-muted-foreground">
            Em pré-atendimento, o Secretário ouve o caso, recolhe os documentos e passa o atendimento para
            você. Para ele também fechar contrato e procuração, informe seus honorários e escolha os modelos
            em Áreas › Personalizar áreas.
          </p>
        )}

        <WizardActions
          isFirst={isFirst}
          onBack={onBack}
          primaryLabel={ativo ? 'Desativar' : 'Ativar Secretário'}
          onPrimary={() => mutation.mutate(!ativo)}
          primaryDisabled={!ativo && !prontidao.pronta}
          loading={mutation.isPending}
        />
      </CardContent>
    </Card>
  );
}
